/**
 * WebSocket hook for real-time scene updates
 *
 * Connects to /api/scenes/stream and handles:
 * - scene_activated
 * - scene_deactivated
 * - scene_expired
 * - scene_executed (for instant scenes)
 */

import { getListScenesQueryKey, getGetSceneQueryKey } from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '@/components/ui';

export interface SceneEvent {
  type: 'scene_activated' | 'scene_deactivated' | 'scene_expired' | 'scene_executed';
  sceneId: string;
  sceneName: string;
  triggerType?: string;
  timestamp: string;
  // For scene_executed events
  fieldsSet?: number;
  fieldsSkipped?: number;
  skippedReason?: string;
}

const RECONNECT_DELAY_BASE = 1000;
const RECONNECT_DELAY_MAX = 30000;
const MAX_RECONNECT_ATTEMPTS = 10;

export function useScenesStream() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);

  const invalidateScenes = useCallback(
    (sceneId?: string) => {
      // Invalidate the list
      queryClient.invalidateQueries({ queryKey: getListScenesQueryKey() });
      // Invalidate specific scene if we have an ID
      if (sceneId) {
        queryClient.invalidateQueries({ queryKey: getGetSceneQueryKey(sceneId) });
      }
    },
    [queryClient]
  );

  const handleEvent = useCallback(
    (event: SceneEvent) => {
      switch (event.type) {
        case 'scene_activated':
          addToast({
            message: `Scene "${event.sceneName}" activated`,
            variant: 'success',
          });
          break;
        case 'scene_deactivated':
          addToast({
            message: `Scene "${event.sceneName}" deactivated`,
            variant: 'info',
          });
          break;
        case 'scene_expired':
          addToast({
            message: `Scene "${event.sceneName}" expired`,
            variant: 'info',
          });
          break;
        case 'scene_executed': {
          // Instant scene execution
          let message = `Scene "${event.sceneName}" executed`;
          if (event.fieldsSkipped && event.fieldsSkipped > 0) {
            message += ` (${event.fieldsSkipped} field${event.fieldsSkipped > 1 ? 's' : ''} skipped`;
            if (event.skippedReason) {
              message += `: ${event.skippedReason}`;
            }
            message += ')';
          }
          addToast({
            message,
            variant: 'success',
          });
          break;
        }
      }
      invalidateScenes(event.sceneId);
    },
    [addToast, invalidateScenes]
  );

  const connect = useCallback(() => {
    // Build WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/scenes/stream`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setConnectionFailed(false);
        reconnectAttemptRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as SceneEvent;
          handleEvent(data);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttemptRef.current),
            RECONNECT_DELAY_MAX
          );
          reconnectAttemptRef.current++;

          reconnectTimeoutRef.current = setTimeout(connect, delay);
        } else {
          setConnectionFailed(true);
        }
      };

      ws.onerror = () => {
        // Error handling is done in onclose
        ws.close();
      };
    } catch {
      setConnectionFailed(true);
    }
  }, [handleEvent]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionFailed,
    reconnect: connect,
  };
}
